/**
 * Operations / Sync & Cleanup hub (Task 53c.4).
 *
 * Single home for the confirm-required Stripe maintenance actions
 * that previously lived inline on the Organisations and Plans pages.
 *
 * Customer sync uses the async-job pattern from Task 54b: the action
 * triggers POST /api/organisations/sync (returns 202+jobId) and polls
 * GET /api/organisations/sync/:jobId every 2s until completion.
 *
 * The other five actions are synchronous (the operations are short).
 * If any of them grow long enough to need the async-job treatment,
 * 54b's StripeSyncJobService is the pattern to copy.
 */
import { OpsActionList, type OpsAction } from '@repro/ui';
import { api } from '../../utils/api';

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_MS = 10 * 60 * 1000;

async function runStripeCustomerSync(): Promise<{ summary: string; warnings?: string[] }> {
  const start = await api.startStripeSyncJob();
  const jobId = start.data.jobId;
  const deadline = Date.now() + MAX_POLL_MS;

  while (Date.now() <= deadline) {
    const poll = await api.getStripeSyncJob(jobId);
    const job = poll.data;
    if (job.status === 'completed') {
      const summary = `Created ${job.created.length}, repaired ${job.removed.length}, verified ${job.verified.length}`;
      return job.errors.length > 0 ? { summary, warnings: job.errors } : { summary };
    }
    if (job.status === 'failed') {
      throw new Error(job.failureReason ?? 'Sync job failed');
    }
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
  }
  throw new Error('Sync job timed out after 10 minutes');
}

const ACTIONS: OpsAction[] = [
  {
    id: 'sync-customers',
    title: 'Sync Customers with Stripe',
    description:
      'Reconcile every organisation with its Stripe customer. Creates missing customers for enabled orgs and clears stale references.',
    confirm:
      'Run customer sync now? Large batches may take a few minutes. Progress will appear here as the job runs.',
    run: runStripeCustomerSync,
  },
  {
    id: 'sync-plans',
    title: 'Sync Plans to Stripe',
    description:
      'Push every local pricing plan to Stripe (creating products and prices that don’t yet exist).',
    run: async () => {
      const result = await api.syncPlans();
      return { summary: result.message ?? 'Plans synced with Stripe' };
    },
  },
  {
    id: 'cleanup-orphaned-plan-refs',
    title: 'Cleanup Orphaned Plan References',
    description:
      'Remove local references to Stripe products/prices that no longer exist. Safe to run; surfaces nothing if everything matches.',
    run: async () => {
      const result = await api.cleanupOrphanedStripeReferences();
      const { cleaned, errors } = result.data;
      const summary =
        cleaned.length > 0
          ? `Cleaned ${cleaned.length} orphaned plan reference(s): ${cleaned.join(', ')}`
          : 'All plans have valid Stripe references.';
      return errors.length > 0 ? { summary, warnings: errors } : { summary };
    },
  },
  {
    id: 'purge-orphaned-products',
    title: 'Purge Orphaned Stripe Products',
    description:
      'Archive Stripe products that no local plan references. This runs against Stripe; the change cannot be undone from the portal.',
    confirm:
      'Purge orphaned Stripe products? This archives Stripe-side products that no local plan references. The action cannot be undone.',
    run: async () => {
      const result = await api.purgeOrphanedStripeProducts();
      const { purged, errors } = result.data;
      const summary =
        purged.length > 0
          ? `Purged ${purged.length} orphaned product(s): ${purged
              .map((p) => `${p.name} (${p.productId})`)
              .join(', ')}`
          : 'No orphaned products found.';
      const warnings =
        errors.length > 0 ? errors.map((e) => `${e.productId}: ${e.error}`) : undefined;
      return warnings ? { summary, warnings } : { summary };
    },
  },
  {
    id: 'cleanup-plans-no-stripe-id',
    title: 'Cleanup Plans Without Stripe IDs',
    description:
      'Remove local pricing plans that lack Stripe product/price IDs and aren’t being used by any organisation.',
    confirm:
      'Cleanup plans without Stripe IDs? Local-only plans not used by any org will be deleted. The action cannot be undone.',
    run: async () => {
      const result = await api.cleanupPlansWithoutStripeIDs();
      const cleaned = result.data?.cleaned ?? [];
      const errors = result.data?.errors ?? [];
      const summary =
        cleaned.length > 0
          ? `Cleaned ${cleaned.length} plan(s) without Stripe IDs: ${cleaned.join(', ')}`
          : 'All plans have Stripe IDs or are in use.';
      return errors.length > 0 ? { summary, warnings: errors } : { summary };
    },
  },
  {
    id: 'cleanup-duplicate-plans',
    title: 'Cleanup Duplicate Plans',
    description:
      'Keep the oldest plan with each name; remove newer duplicates. Use after manual edits introduced collisions.',
    confirm:
      'Cleanup duplicate plans? The oldest plan per name is kept; newer duplicates are removed. The action cannot be undone.',
    run: async () => {
      const result = await api.cleanupDuplicatePlans();
      const cleaned = result.data?.cleaned ?? [];
      const errors = result.data?.errors ?? [];
      const summary =
        cleaned.length > 0
          ? `Removed ${cleaned.length} duplicate plan(s): ${cleaned.join(', ')}`
          : 'No duplicate plans found.';
      return errors.length > 0 ? { summary, warnings: errors } : { summary };
    },
  },
];

export default function SyncCleanup() {
  return (
    <div className="space-y-4">
      <div className="bg-base-100 shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold text-base-content">Sync & Cleanup</h1>
        <p className="text-sm text-base-content/70 mt-1">
          Confirm-required Stripe maintenance operations. Each action runs against the live
          environment — read the description before pressing Run.
        </p>
      </div>
      <OpsActionList actions={ACTIONS} />
    </div>
  );
}
